// Path: src/components/workshoppage/ChatPanel.tsx
import React, { useRef, useEffect, useState } from 'react';
import { Send, Loader2, Trash2, Trash, Paperclip, ImageIcon, X, MessageSquare, Scissors, Repeat, Copy, Check, Download, FileCheck, ArrowUp, ArrowDown, Square } from 'lucide-react';
import { MessageRenderer } from './MessageRenderer';
import { ReasoningMarquee } from '../common/ReasoningMarquee';
import { type LocalChatMessage } from '../../types/workshop';
import { api, type SlideContextResponse, deleteSingleSlideChatMessage } from '../../lib/api';
import TokenWarningModal from '../common/TokenWarningModal';
import { buildContinueChunkPrompt } from '../../lib/htmlChunkAssembler';

interface Props {
  messages: LocalChatMessage[];
  setMessages?: (messages: LocalChatMessage[]) => void;
  isChatting: boolean;
  prompts: any[];
  selectedPromptId: number | null;
  onSelectPrompt: (id: number) => void;
  artifactId: number;
  slideContext: SlideContextResponse | null;
  onSendMessage: (content: string, files: string[], model: 'gemini' | 'nanobanana') => void;
  onApplyCode: (code: string) => void;
  onDeleteMessage: (id: number) => void;
  onClearHistory: () => void;
  onAudit: () => void;
  isAuditing: boolean;
  currentHtml?: string;
  currentReasoning?: string;
  onStopGeneration?: () => void;
}

export const ChatPanel = ({
  messages, setMessages, isChatting, prompts, selectedPromptId,
  onSelectPrompt, onSendMessage, onApplyCode, onDeleteMessage, onClearHistory, artifactId, slideContext,
  onAudit, isAuditing, currentHtml = '', currentReasoning = '', onStopGeneration
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');

  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedModel, setSelectedModel] = useState<'gemini' | 'nanobanana'>(() => {
    const saved = localStorage.getItem('workshop_selected_model');
    return (saved === 'gemini' || saved === 'nanobanana') ? saved : 'gemini';
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [copiedMsgId, setCopiedMsgId] = useState<string | number | null>(null);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [warningTokenCount, setWarningTokenCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, isChatting, currentReasoning]);

  useEffect(() => {
    localStorage.setItem('workshop_selected_model', selectedModel);
  }, [selectedModel]);

  useEffect(() => {
    if (messages.length > 0 && !isChatting) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.input_tokens && lastMessage.input_tokens >= 190000) {
        setWarningTokenCount(lastMessage.input_tokens);
        setShowTokenWarning(true);
      }
    }
  }, [messages, isChatting]);

  const handleSend = () => {
    if ((!input.trim() && selectedFiles.length === 0) || isChatting) return;

    if (selectedModel === 'nanobanana') {
      setShowConfirmModal(true);
      return;
    }

    onSendMessage(input, selectedFiles, selectedModel);

    setInput('');
    setSelectedFiles([]);
  };

  const confirmSend = (model: 'gemini' | 'nanobanana') => {
    if (model !== selectedModel) {
      setSelectedModel(model);
    }

    onSendMessage(input, selectedFiles, model);

    setInput('');
    setSelectedFiles([]);
    setShowConfirmModal(false);
  };

  const handleCutout = () => {
    if (selectedFiles.length === 0) {
      alert('请先上传图片');
      return;
    }

    // 在输入内容前拼接"抠图："
    const cutoutContent = `抠图：${input}`;

    // 切换到nanobanana模型并立即发送消息
    setSelectedModel('nanobanana');
    onSendMessage(cutoutContent, selectedFiles, 'nanobanana');

    // 重置状态
    setInput('');
    setSelectedFiles([]);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // 调用后端上传接口 (使用正确的API路径 - 相对于baseURL)
      const res = await api.post('/upload/oss/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { artifact_id: artifactId } // 使用传递的artifactId参数
      });

      // 后端返回 { file_url: "...", file_name: "...", file_size: ... }
      const url = res.data.file_url;
      if (url) {
        setSelectedFiles(prev => [...prev, url]);
      }
    } catch (err) {
      console.error('Upload failed', err);
      alert('上传失败，请检查网络或后端接口');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageUrlPattern = /^(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp)(?:\?[^\s]*)?)$/i;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind === 'file' && (item.type.startsWith('image/') || item.type === 'video/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setIsUploading(true);
          try {
            const formData = new FormData();
            formData.append('file', file);

            const res = await api.post('/upload/oss/', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              params: { artifact_id: artifactId }
            });

            const url = res.data.file_url;
            if (url) {
              setSelectedFiles(prev => [...prev, url]);
            }
          } catch (err) {
            console.error('Paste upload failed', err);
            alert('粘贴图片上传失败');
          } finally {
            setIsUploading(false);
          }
        }
      } else if (item.kind === 'string' && item.type === 'text/plain') {
        const pastedText = e.clipboardData.getData('text/plain');
        if (pastedText && input.trim() === '') {
          const match = pastedText.match(imageUrlPattern);
          if (match) {
            e.preventDefault();
            setSelectedFiles(prev => [...prev, match[1]]);
            return;
          }
        }
      }
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteClick = (rawId: string | number) => {
    const id = Number(rawId);
    if (!isNaN(id)) onDeleteMessage(id);
  };

  const handleResendMessage = (aiMsgIndex: number) => {
    // 找到前一条用户消息
    const userMsgIndex = aiMsgIndex - 1;
    if (userMsgIndex >= 0 && messages[userMsgIndex].role === 'user') {
      const userMsg = messages[userMsgIndex];
      const userMsgContent = userMsg.content;
      const aiMsg = messages[aiMsgIndex];

      // 检查AI消息是否有ID
      if (aiMsg.id && typeof aiMsg.id === 'number') {
        // 先删除一对儿消息（当前AI回复和前一条用户消息）
        onDeleteMessage(aiMsg.id);

        // 重新发送用户消息
        // 由于删除操作是异步的，我们需要等待一下再发送
        setTimeout(() => {
          onSendMessage(userMsgContent, (userMsg.files || []).map(file => file.oss_url), 'gemini');
        }, 500);
      } else {
        alert("消息正在处理中，请稍候再试");
      }
    }
  };

  const handleDeleteUserMessage = async (msgId: number, msgIndex: number) => {
    if (!window.confirm("确定删除这条消息？")) return;
    try {
      const res = await deleteSingleSlideChatMessage(msgId);
      // 从状态中删除这条消息
      if (setMessages) {
        setMessages(messages.filter((_, idx) => idx !== msgIndex));
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.detail || e.message || "删除失败";
      alert(`删除失败: ${errorMessage}`);
    }
  };

  const handleResendUserMessage = async (msgId: number, msgIndex: number) => {
    if (!window.confirm("确定重新发送这条消息？")) return;
    try {
      // 获取当前用户消息
      const userMsg = messages[msgIndex];
      const userMsgContent = userMsg.content;
      const userMsgFiles = userMsg.files || [];

      // 先删除这条消息
      await deleteSingleSlideChatMessage(msgId);

      // 从状态中删除这条消息
      if (setMessages) {
        setMessages(messages.filter((_, idx) => idx !== msgIndex));
      }

      // 重新发送用户消息
      setTimeout(() => {
        onSendMessage(userMsgContent, (userMsgFiles || []).map(file => file.oss_url), 'gemini');
      }, 500);
    } catch (e: any) {
      const errorMessage = e.response?.data?.detail || e.message || "重新发送失败";
      alert(`重新发送失败: ${errorMessage}`);
    }
  };

  const handleCopyUserMessage = async (msgIndex: number) => {
    try {
      const userMsg = messages[msgIndex];
      const userMsgContent = userMsg.content;

      await navigator.clipboard.writeText(userMsgContent);

      alert("消息已成功复制到剪贴板！");
    } catch (e: any) {
      const errorMessage = e.message || "复制失败";
      alert(`复制失败: ${errorMessage}`);
    }
  };

  const handleCopyAIMessage = async (msgId: string | number, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMsgId(msgId);
      setTimeout(() => setCopiedMsgId(null), 2000);
    } catch (e: any) {
      const errorMessage = e.message || "复制失败";
      alert(`复制失败: ${errorMessage}`);
    }
  };

  const handleExportAIMessage = async (content: string) => {
    const defaultFileName = `AI回复_${new Date().toLocaleString('zh-CN').replace(/[/:]/g, '-')}.txt`;
    
    try {
      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: defaultFileName,
          types: [{
            description: '文本文件',
            accept: { 'text/plain': ['.txt'] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(content);
        await writable.close();
      } else {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = defaultFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        console.error('导出失败:', e);
        alert(`导出失败: ${e.message || '未知错误'}`);
      }
    }
  };

  return (
    <div className="w-[40%] flex flex-col bg-white border-r relative z-0 h-full">
      {/* 消息列表区域 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-10 text-sm">👋 开始你的设计之旅吧！</div>
        )}

        {messages.map((msg, idx) => (
          <div key={msg.id || idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} group/msg items-end`}>

            {/* 用户消息的操作按钮组 */}
            {msg.role === 'user' && msg.id && typeof msg.id === 'number' && (
              <div className="flex flex-col items-center gap-1">
                <button
                  onClick={() => handleDeleteUserMessage(Number(msg.id), idx)}
                  className="p-1.5 transition-colors text-gray-300 hover:text-red-500 hover:bg-red-50 rounded cursor-pointer"
                  title="删除这条消息"
                >
                  <Trash2 size={14} />
                </button>
                <button
                  onClick={() => handleCopyUserMessage(idx)}
                  className="p-1.5 transition-colors text-gray-300 hover:text-green-500 hover:bg-green-50 rounded cursor-pointer"
                  title="复制这条消息"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                </button>

              </div>
            )}

            <div className={`max-w-[90%] p-3 rounded-2xl text-sm shadow-sm relative ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'}`}>

              {/* 渲染消息内容 (含文本和图片) */}
              {/* @ts-ignore: LocalChatMessage 还没更新类型定义，这里先忽略 */}
              <MessageRenderer
                content={msg.content}
                files={msg.files}
                patched_html={msg.patched_html}
                patch_result={msg.patch_result}
                onApplyCode={onApplyCode}
                partial={msg.partial}
                partialReason={msg.partialReason}
              />

              {msg.role === 'assistant' && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center min-h-[20px]">
                  <div className="text-[10px] text-gray-400 font-mono flex items-center gap-1 flex-wrap">
                    {msg.files?.some(f => f.file_type === 'image_output') ? (
                      <>
                        <span title="pay">💰¥{(msg.output_money ?? 0).toFixed(4)}</span>
                        {' / '}
                        <span title="total_money">🏦¥{(msg.total_money ?? 0).toFixed(4)}</span>
                      </>
                    ) : (
                      <>
                        <span title="input_token" className="inline-flex items-center gap-0.5"><ArrowUp size={10} className="text-green-500" />{msg.input_tokens ?? 0}</span>
                        {' / '}
                        <span title="output_token" className="inline-flex items-center gap-0.5"><ArrowDown size={10} className="text-orange-500" />{msg.output_tokens ?? 0}</span>
                        {' / '}
                        <span title="pay">💰¥{((msg.input_money ?? 0) + (msg.output_money ?? 0)).toFixed(4)}</span>
                        {' / '}
                        <span title="total_money">🏦¥{(msg.total_money ?? 0).toFixed(4)}</span>
                      </>
                    )}
                  </div>

                  {msg.id && msg.id !== 'streaming' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleCopyAIMessage(msg.id, msg.content)}
                        className={`p-1.5 transition-colors rounded cursor-pointer ${
                          copiedMsgId === msg.id 
                            ? 'text-green-500 bg-green-50' 
                            : 'text-gray-300 hover:text-green-500 hover:bg-green-50'
                        }`}
                        title="复制消息内容"
                      >
                        {copiedMsgId === msg.id ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      <button
                        onClick={() => handleExportAIMessage(msg.content)}
                        className="p-1.5 transition-colors text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded cursor-pointer"
                        title="导出为TXT文件"
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(msg.id)}
                        className="p-1.5 transition-colors text-gray-300 hover:text-red-500 hover:bg-red-50 rounded cursor-pointer"
                        title="删除此对话"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {currentReasoning && (
          <ReasoningMarquee text={currentReasoning} variant="reasoning" />
        )}

        {isChatting && (
          <div className="flex justify-start">
            <div className="bg-gray-100 p-2 rounded-lg rounded-bl-none text-xs text-gray-500 flex items-center gap-1 animate-pulse">
              <Loader2 size={12} className="animate-spin" /> AI thinking...
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* 底部输入区域 */}
      <div className="p-4 bg-white border-t">

        {/* 工具栏：新增按钮行 + 模型选择 + 系统角色 */}
        <div className="mb-3">
          {/* 新增按钮行 */}
          <div className="flex gap-2 mb-2 justify-between">
            <div className="flex gap-2">
              {/* 清空聊天记录按钮 */}
              {messages.length > 0 && (
                <button
                  onClick={() => { setSelectedModel('gemini'); onClearHistory(); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                  title="清空所有聊天记录"
                >
                  <Trash size={14} /> 清空记录
                </button>
              )}

              {/* Start按钮 - 只在slide_index等于1且聊天记录为空时显示 */}
              {slideContext?.slide.slide_index === 1 && messages.length === 0 && (
                <button
                  onClick={() => {
                    setSelectedModel('gemini');
                    const stageName = slideContext.stage.title;
                    const lessonNumber = slideContext.lesson.sort_order;
                    const startMessage = `开始开发${stageName}第${lessonNumber}课课件[No.${artifactId}]`;
                    onSendMessage(startMessage, [], 'gemini');
                  }}
                  disabled={isChatting}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-transparent hover:border-green-100 disabled:opacity-50"
                  title="开始开发课件"
                >
                  <MessageSquare size={14} /> Start
                </button>
              )}
            </div>
            
            {/* 审核课件按钮 */}
            <button
              onClick={onAudit}
              disabled={isAuditing}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-100 disabled:opacity-50"
              title="审核课件"
            >
              {isAuditing ? <Loader2 size={14} className="animate-spin" /> : <FileCheck size={14} />} 审核课件
            </button>
          </div>
          {/* 继续开发按钮行 */}
          <div className="flex gap-2">
            {/* 继续开发当前页设计按钮 */}
            {slideContext?.slide.slide_index !== 1 && (
              <button
                onClick={() => {
                  setSelectedModel('gemini');
                  const slideIndex = slideContext?.slide.slide_index || 0;
                  setInput(`继续开发第${slideIndex}页设计`);
                }}
                disabled={isChatting}
                className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-pink-400 to-pink-500 rounded-full shadow-md hover:shadow-lg hover:from-pink-500 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title="继续开发当前页设计"
              >
                生成设计
              </button>
            )}

            {/* 继续剩余设计按钮 */}
            <button
              onClick={() => {
                setSelectedModel('gemini');
                const slideIndex = slideContext?.slide.slide_index || 0;
                if (slideIndex === 1) {
                  setInput(`回看前一条消息你刚生成过的本课50页完整规划中最后1~2行，继续仅输出以上50页完整课件规划中剩余课件页的设计: 不要再从50页课件完整设计开头开始生成！`);
                } else {
                  setInput(`回看前一条消息你刚生成过的本页课件设计的最后1~2行，继续仅输出本页设计剩余文本（不是HTML代码也不是页边教案）。不要再从本页设计开头开始生成！`);
                }
              }}
              disabled={isChatting}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100 disabled:opacity-50"
              title="继续剩余设计"
            >
              继续剩余设计
            </button>

            {/* 继续开发当前页HTML代码按钮 */}
            <button
              onClick={() => {
                setSelectedModel('gemini');
                const slideIndex = slideContext?.slide.slide_index || 0;
                let message = `继续开发第${slideIndex}页完整HTML代码`;
                if (slideIndex === 2) {
                  message = `继续开发第${slideIndex}页完整HTML代码\n\n主视频url列表:`;
                } else if (slideIndex > 2) {
                  message = `继续开发第${slideIndex}页完整HTML代码\n并将以下资源url加入网页HTML代码中对应元素属性中：\n图片资源url列表：\n\n音效资源url列表：\n(使用浏览器内置自带音效合成API)`;
                }
                setInput(message);
              }}
              disabled={isChatting}
              className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-violet-400 to-violet-500 rounded-full shadow-md hover:shadow-lg hover:from-violet-500 hover:to-violet-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="继续开发当前页HTML代码"
            >
              生成HTML代码
            </button>
            {/* 继续开发当前页HTML代码按钮 */}
            <button
              onClick={() => {
                setSelectedModel('gemini');
                const prompt = buildContinueChunkPrompt(currentHtml);
                setInput(prompt);
              }}
              disabled={isChatting}
              className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-transparent hover:border-purple-100 disabled:opacity-50"
              title="继续开发当前页HTML代码"
            >
              继续剩余代码
            </button>
            {/* 继续生成当前页页边教案按钮 */}
            <button
              onClick={() => {
                setSelectedModel('gemini');
                const slideIndex = slideContext?.slide.slide_index || 0;
                setInput(`继续生成第${slideIndex}页页边教案`);
              }}
              disabled={isChatting}
              className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full shadow-md hover:shadow-lg hover:from-emerald-500 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="继续生成当前页页边教案"
            >
              生成页边教案
            </button>
          </div>

          {/* 模型选择 + 系统角色 */}
          <div className="flex gap-2 flex-1">
            {/* 🤖 模型选择 */}
            <div className="w-1/2">
              <select
                className="w-full text-sm p-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value as any)}
              >
                <option value="gemini">Chat (文字聊天|图像理解)</option>
                <option value="nanobanana">Image (绘画|修图|分镜制作)</option>
              </select>
            </div>

            {/* 🎭 系统角色 */}
            <div className="w-1/2">
              <select
                className="w-full text-sm p-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                value={selectedPromptId || ''}
                onChange={(e) => onSelectPrompt(Number(e.target.value))}
              >
                {prompts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* 📎 图片预览区 */}
        {selectedFiles.length > 0 && (
          <div className="flex gap-2 mb-2 overflow-x-auto pb-2">
            {selectedFiles.map((url, idx) => {
              // 检查是否为视频文件
              const isVideo = url.toLowerCase().endsWith('.mp4');

              return (
                <div key={idx} className="relative group flex-shrink-0">
                  {isVideo ? (
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 relative">
                      <button
                        onClick={() => window.open(url, '_blank')}
                        className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors rounded-lg"
                        title="在新窗口打开视频"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                      </button>
                    </div>
                  ) : (
                    <img src={url} alt="upload" className="w-16 h-16 object-cover rounded-lg border border-gray-200" />
                  )}
                  <button
                    onClick={() => removeFile(idx)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={10} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="relative">
          <textarea
            className="w-full p-3 pr-20 border rounded-xl text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 focus:bg-white shadow-inner"
            rows={3}
            placeholder={selectedModel === 'nanobanana' ? "描述你想生成的图片... (支持Ctrl+V粘贴图片)" : "描述修改需求... (支持Ctrl+V粘贴图片)"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            onPaste={handlePaste}
          />

          <div className="right-2 bottom-2 flex justify-between gap-1">
            {/* 左侧按钮组：上传和抠图 */}
            <div className="flex gap-1">
              {/* 上传按钮 */}
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/mp4"
                onChange={handleUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isChatting}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="上传图片"
              >
                {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              </button>

              {/* 抠图按钮 */}
              <button
                onClick={handleCutout}
                disabled={isChatting || selectedFiles.length === 0}
                className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                title="抠图"
              >
                <Scissors size={16} />
              </button>
            </div>

            {/* 右侧发送/停止按钮 */}
            {isChatting ? (
              <button
                onClick={onStopGeneration}
                className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                title="停止生成"
              >
                <Square size={16} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={(!input.trim() && selectedFiles.length === 0)}
                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 确认对话框 */}
      {showConfirmModal && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-yellow-500/10 rounded-full">
                <ImageIcon className="text-yellow-500 w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">确认生成图片？</h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  您当前选择了 <span className="text-yellow-400 font-medium">Nanobanana 生图模式</span>。
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  如果只是想修改代码或聊天，请切换回文本模型以节省 Token。
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => confirmSend('nanobanana')}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <ImageIcon size={16} />
                确认绘图
              </button>

              <button
                onClick={() => confirmSend('gemini')}
                className="w-full py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
              >
                <MessageSquare size={16} />
                转为文本模型 (Gemini)
              </button>

              <button
                onClick={() => setShowConfirmModal(false)}
                className="w-full py-2 text-xs text-gray-400 hover:text-gray-300 mt-1"
              >
                取消发送
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token 超限警告模态框 */}
      <TokenWarningModal
        isOpen={showTokenWarning}
        onClose={() => setShowTokenWarning(false)}
        currentTokens={warningTokenCount}
      />
    </div>
  );
};
